import { useState, useEffect, useCallback } from 'react';
import { QuestionMarkService } from '../services/questionMarkService';

/**
 * Hook to fetch and access questionmark help configurations
 * Returns a map of controlId -> helpText for efficient lookups
 */
export function useQuestionMarks() {
  const [questionMarks, setQuestionMarks] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchQuestionMarks = async () => {
      try {
        setIsLoading(true);
        const configs = await QuestionMarkService.getQuestionMarks();
        
        if (mounted) {
          // Convert array to Map for O(1) lookups
          const configMap = new Map<string, string>();
          configs.forEach(config => {
            configMap.set(config.controlId, config.helpText);
          });
          setQuestionMarks(configMap);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch questionmarks'));
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchQuestionMarks();

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Get help text for a control by its label (case-insensitive)
   */
  const getHelpText = useCallback((controlLabel: string): string | null => {
    const normalizedLabel = controlLabel.toLowerCase().trim();
    return questionMarks.get(normalizedLabel) ?? null;
  }, [questionMarks]);

  /**
   * Check if a control has help text configured
   */
  const hasHelp = useCallback((controlLabel: string): boolean => {
    const normalizedLabel = controlLabel.toLowerCase().trim();
    return questionMarks.has(normalizedLabel);
  }, [questionMarks]);

  return {
    questionMarks,
    getHelpText,
    hasHelp,
    isLoading,
    error
  };
}

